import { Component } from '@theme/component';
import { StandardEvents } from '@shopify/events';

/**
 * Upsell popup offering the 18-piece brush collection when any lash is added to cart.
 * Excluded from home page.
 *
 * @typedef {object} Refs
 * @property {HTMLDialogElement} dialog - The popup dialog.
 * @property {HTMLButtonElement} addButton - Add to bag button.
 * @property {HTMLElement} addText - Button text label.
 *
 * @extends {Component<Refs>}
 */
class LashBrushUpsell extends Component {
  requiredRefs = ['dialog', 'addButton', 'addText'];
  #openScheduled = false;

  connectedCallback() {
    super.connectedCallback();

    // Never activate on home page
    if (this.#isHomePage()) return;

    document.addEventListener(StandardEvents.cartLinesUpdate, this.#handleCartLinesUpdate);
    this.addEventListener('click', this.#handleClick);
    this.refs.dialog.addEventListener('click', this.#handleDialogClick);
    this.#setupFetchInterceptor();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    document.removeEventListener(StandardEvents.cartLinesUpdate, this.#handleCartLinesUpdate);
    this.removeEventListener('click', this.#handleClick);
    this.refs.dialog.removeEventListener('click', this.#handleDialogClick);
  }

  #isHomePage() {
    const path = window.location.pathname.replace(/\/+$/, '');
    return path === '' || path === '/' || document.body.classList.contains('template-index');
  }

  /**
   * Listen to cartLinesUpdate standard event
   * @param {import('@shopify/events').CartLinesUpdateEvent} event
   */
  #handleCartLinesUpdate = (event) => {
    if (this.#isHomePage()) return;
    if (event.action !== 'add' || !this.#isLashAdd(event)) return;

    const openDialog = () => {
      if (this.refs.dialog.open || this.#openScheduled) return;
      this.#openScheduled = true;
      requestAnimationFrame(() => {
        this.#openScheduled = false;
        if (this.refs.dialog.open || !this.isConnected) return;
        this.#resetButtonState();
        this.refs.dialog.showModal();
      });
    };

    if (event.promise && typeof event.promise.then === 'function') {
      event.promise
        .then(({ detail }) => {
          if (!detail?.didError) openDialog();
        })
        .catch(() => openDialog());
    } else {
      openDialog();
    }
  };

  /**
   * Intercept fetch calls to /cart/add to catch lash additions across any forms
   */
  #setupFetchInterceptor() {
    if (window._lashBrushInterceptorActive) return;
    window._lashBrushInterceptorActive = true;

    const originalFetch = window.fetch;
    const self = this;

    window.fetch = async function (...args) {
      const response = await originalFetch.apply(this, args);
      try {
        if (self.#isHomePage()) return response;
        const url = typeof args[0] === 'string' ? args[0] : (args[0]?.url || '');
        if (url.includes('/cart/add') && response.ok) {
          const clone = response.clone();
          clone.json().then((data) => {
            const addedVariantId = String(data.id || data.variant_id || '');
            const addedHandle = String(data.handle || '');
            if (self.#isLashVariantOrHandle(addedVariantId, addedHandle)) {
              self.#resetButtonState();
              self.refs.dialog.showModal();
            }
          }).catch(() => {});
        }
      } catch (e) {}
      return response;
    };
  }

  /**
   * Check if added item is a lash product
   * @param {import('@shopify/events').CartLinesUpdateEvent} event
   */
  #isLashAdd(event) {
    const productForm = event.target instanceof Element ? event.target.closest('[data-product-id]') : null;
    const productId = String(productForm?.getAttribute('data-product-id') ?? event.detail?.productId ?? '');

    const lashProductIds = new Set(
      Array.from(this.querySelectorAll('[data-lash-product-id]'), (item) => item.dataset.lashProductId)
    );
    if (productId && lashProductIds.has(productId)) return true;

    const allLashVariantIds = new Set();
    this.querySelectorAll('[data-lash-variant-ids]').forEach((el) => {
      (el.dataset.lashVariantIds || '').split(',').forEach((vid) => {
        if (vid.trim()) allLashVariantIds.add(vid.trim());
      });
    });

    if (Array.isArray(event.lines)) {
      return event.lines.some((line) => {
        const variantId = String(line.merchandiseId || '').split('/').pop();
        return allLashVariantIds.has(variantId);
      });
    }

    return true;
  }

  #isLashVariantOrHandle(variantId, handle) {
    const lashHandles = new Set(['killer-wing', 'snatched', 'doll-face', 'showstopper', 'effortless', 'angel-eyes']);
    if (handle && lashHandles.has(handle.toLowerCase())) return true;

    const allLashVariantIds = new Set();
    this.querySelectorAll('[data-lash-variant-ids]').forEach((el) => {
      (el.dataset.lashVariantIds || '').split(',').forEach((vid) => {
        if (vid.trim()) allLashVariantIds.add(vid.trim());
      });
    });

    return Boolean(variantId && allLashVariantIds.has(variantId));
  }

  #resetButtonState() {
    this.refs.addButton.disabled = false;
    this.refs.addText.textContent = this.refs.addButton.dataset.defaultText || this.refs.addText.textContent;
  }

  /** @param {MouseEvent} event */
  #handleClick = (event) => {
    if (!(event.target instanceof Element)) return;

    if (event.target.closest('[data-dialog-close]')) {
      this.refs.dialog.close();
      return;
    }

    if (event.target.closest('.brush-upsell__btn-add') || event.target === this.refs.addButton) {
      this.#addBrushToBag();
    }
  };

  /** @param {MouseEvent} event */
  #handleDialogClick = (event) => {
    if (event.target === this.refs.dialog) {
      this.refs.dialog.close();
    }
  };

  async #addBrushToBag() {
    const variantId = this.dataset.brushVariantId;
    if (!variantId || this.refs.addButton.disabled) return;

    this.refs.addButton.disabled = true;
    if (!this.refs.addButton.dataset.defaultText) {
      this.refs.addButton.dataset.defaultText = this.refs.addText.textContent;
    }
    this.refs.addText.textContent = 'Adding to bag...';

    try {
      const response = await fetch('/cart/add.js', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json'
        },
        body: JSON.stringify({
          id: variantId,
          quantity: 1
        })
      });

      if (response.ok) {
        this.refs.addText.textContent = 'Added to Bag ✓';

        // Refresh cart drawer or cart count
        try {
          const cartRes = await fetch('/cart.js');
          const cartData = await cartRes.json();
          document.querySelectorAll('.cart-count, [data-cart-count]').forEach((el) => {
            el.textContent = cartData.item_count;
          });
          // If theme has cart-drawer, open it or notify
          const cartDrawer = document.querySelector('cart-drawer');
          if (cartDrawer && typeof cartDrawer.open === 'function') {
            setTimeout(() => {
              this.refs.dialog.close();
              cartDrawer.open();
            }, 600);
            return;
          }
        } catch (e) {}

        setTimeout(() => {
          this.refs.dialog.close();
        }, 1000);
      } else {
        this.refs.addText.textContent = 'Could not add to bag';
        setTimeout(() => this.#resetButtonState(), 1800);
      }
    } catch (err) {
      this.refs.addText.textContent = 'Error adding item';
      setTimeout(() => this.#resetButtonState(), 1800);
    }
  }
}

if (!customElements.get('lash-brush-upsell')) {
  customElements.define('lash-brush-upsell', LashBrushUpsell);
}
